import React from 'react'
import Stack from '@mui/material/Stack'
import { useTranslation } from 'react-i18next'
import {
  DataGridPro,
  GridColDef,
  GridRowParams,
  GridValueGetterParams,
  getGridSingleSelectOperators,
  useGridApiRef
} from '@mui/x-data-grid-pro'

import { IModel } from '@cbgms/api/modules/vehicles'
import { useArrowScrollHandler } from '@carsys/ui/src/helpers/keyboard/use-arrow-scroll-handler'
import { useFilter } from 'app/hooks/useFilter'
import { GridHeaderFilterSingleSelect } from '../../grid/GridHeaderFilterSingleSelect'
import { DataGridToolbarWithSearch } from '../../grid/GridToolBarWithSearch'
import { useDataGridLocaleText } from '@cbgms/components/list/use-data-grid-locale-text'
import { useOnEnterHandler } from '@carsys/ui/src/helpers/keyboard/use-on-enter-handler'
import {
  buildYearDisplayValue,
  buildYearRange,
  generateUniqueOptions,
  getRowByIndex,
  getVisibleRowCount,
  onKeyScroll,
  parseVehicleDate,
  sortFilterOptions
} from '../../grid/Helpers'
import { gridHeaderStyling } from '../../grid/Styling'
import { GridDateRangeIncludes, extraDateRangeFilterModel } from '../../grid/GridDateRangeIncludes'
import { SortDirection } from '@cbgms/api'

export interface IModelSelectionProps {
  onModelSelect: (modelKey: string, model: IModel) => void
  models: IModel[]
}

enum columnNames {
  ModelName = 'ModelName',
  Remarks = 'Remarks',
  BuildYears = 'BuildYears'
}

export const ModelList: React.FC<IModelSelectionProps> = ({ onModelSelect, models }) => {
  const { t } = useTranslation()
  const localeText = useDataGridLocaleText()
  const apiRef = useGridApiRef()

  const [filteredTableData, setFilter, filter] = useFilter(models, extraDateRangeFilterModel)
  const [rowCount, setRowCount] = React.useState(filteredTableData.length)

  const { currentFocus, hasRowFocus } = useArrowScrollHandler(rowCount, 1, (index: number) => onKeyScroll(apiRef, index))
  useOnEnterHandler(hasRowFocus, () => {
    const { model } = getRowByIndex(apiRef, currentFocus)
    onModelSelect(model.ModelID, model as IModel)
  })

  React.useEffect(() => {
    apiRef.current.subscribeEvent('stateChange', () => {
      const count = getVisibleRowCount(apiRef.current.state)
      setRowCount(count)
    })
  }, [apiRef])

  const buildYears = React.useMemo(() => {
    const result = filteredTableData.reduce(
      (result, model) => {
        const madeFrom = parseVehicleDate(model.MadeFrom).year()
        const madeUnit = parseVehicleDate(model.MadeUntil).year()
        if (result.from > madeFrom) {
          result.from = madeFrom
        }
        if (result.until < madeUnit) {
          result.until = madeUnit
        }
        return result
      },
      { from: new Date().getFullYear(), until: 0 }
    )

    const buildYearRanges = buildYearRange(result.from, result.until)
    const uniqueOptions = generateUniqueOptions(buildYearRanges)
    const sortedOptions = sortFilterOptions(uniqueOptions, SortDirection.Descending)
    return { options: sortedOptions }
  }, [filteredTableData, generateUniqueOptions, buildYearRange, sortFilterOptions])

  const columns = React.useMemo<GridColDef[]>(() => {
    const modelNameOption = sortFilterOptions(
      generateUniqueOptions(filteredTableData.map(model => ({ value: model.ModelName, label: model.ModelName })))
    )
    const modelRemarkOption = sortFilterOptions(
      generateUniqueOptions(filteredTableData.map(model => ({ value: model.Remarks, label: model.Remarks })))
    )
    const hasRemarks = !!models.find(a => !!a.Remarks)

    const columns: GridColDef[] = []
    columns.push({
      field: columnNames.ModelName,
      align: 'left',
      minWidth: 200,
      headerName: t('vehicle.model'),
      disableColumnMenu: true,
      filterable: !!modelNameOption.length,
      valueOptions: modelNameOption,
      type: 'singleSelect',
      filterOperators: getGridSingleSelectOperators()
        .filter(op => op.value === 'is')
        .map(operator => ({
          ...operator,
          InputComponent: GridHeaderFilterSingleSelect,
          InputComponentProps: { options: modelNameOption }
        }))
    })
    if (hasRemarks) {
      columns.push({
        field: columnNames.Remarks,
        align: 'left',
        minWidth: 200,
        headerName: t('vehicle.remarks'),
        disableColumnMenu: true,
        filterable: !!modelRemarkOption.length,
        valueOptions: modelRemarkOption,
        type: 'singleSelect',
        filterOperators: getGridSingleSelectOperators().map(operator => ({
          ...operator,
          InputComponent: GridHeaderFilterSingleSelect,
          InputComponentProps: { options: modelRemarkOption }
        }))
      })
    }
    columns.push({
      field: columnNames.BuildYears,
      align: 'right',
      minWidth: 200,
      headerName: t('vehicle.construction_year_range'),
      disableColumnMenu: true,
      filterable: !!buildYears.options.length,
      valueGetter: (model: GridValueGetterParams<IModel>) => buildYearDisplayValue(t, model.row.MadeFrom, model.row.MadeUntil),
      filterOperators: GridDateRangeIncludes({ options: buildYears.options }, 'model')
    })
    return columns
  }, [buildYears.options, filteredTableData, buildYears, models])

  return (
    <Stack spacing={2} data-testid='vehicle-modelselection'>
      <DataGridPro
        apiRef={apiRef}
        autoHeight
        autosizeOnMount
        autosizeOptions={{
          includeOutliers: true,
          includeHeaders: true
        }}
        density='compact'
        disableColumnFilter
        unstable_headerFilters
        disableMultipleRowSelection
        localeText={localeText}
        columns={columns}
        rows={filteredTableData}
        pagination={false}
        hideFooterSelectedRowCount
        disableVirtualization // unmounts rows on testing, not necesary for performance on this specific grid
        getRowId={(row: IModel) => [row.ModelID, row.ModelName].toString()}
        slots={{
          headerFilterMenu: null,
          toolbar: DataGridToolbarWithSearch
        }}
        slotProps={{
          toolbar: { name: 'ModelSelectorSearchInput', placeholder: t('Vehicle.SearchModel'), setFilter, filter }
        }}
        onRowClick={(params: GridRowParams<IModel>) => {
          onModelSelect(params.row.ModelID, params.row)
        }}
        sx={gridHeaderStyling}
      />
    </Stack>
  )
}
